import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ScreenValue } from '../components/analytics/screen'

/**
 * Analytics data layer.
 *
 * Every hook here reads a server-side aggregate. Nothing is summed in the
 * browser any more: the previous hooks pulled raw events under a LIMIT (the
 * client-detail view asked for 10,000) and added them up, so a busy share
 * reported a truncated sample as a total.
 */

export interface AnalyticsFilters {
  from?: string
  to?: string
  seed?: string
  shareId?: number
  workspaceId?: number
  /** Repeatable — the multi-selects send one param per choice. */
  version?: string[]
  app?: string[]
  email?: string[]
  visitorId?: string
  domain?: string
  kind?: string[]
  /** The EFFECTIVE classification, after any dictionary override. */
  interaction?: string[]
  /** What the classifier decided, before any override. Technical view only. */
  rawInteraction?: string[]
  eventName?: string
  country?: string[]
  device?: string[]
  browser?: string[]
  /** OS and major version as one value — "Android 13". */
  platform?: string[]
  excludeInternal?: boolean
}

export type GroupKey =
  | 'seed' | 'share' | 'workspace' | 'version' | 'app' | 'path' | 'action'
  | 'user' | 'domain' | 'visitor' | 'country' | 'city' | 'kind' | 'event' | 'attribution'
  | 'interaction' | 'rawInteraction' | 'device' | 'browser' | 'os' | 'platform' | 'osVersion'

export interface Labels {
  seeds: Record<string, string>
  shares: Record<string, string>
  workspaces: Record<string, string>
}

export const EMPTY_LABELS: Labels = { seeds: {}, shares: {}, workspaces: {} }

export interface Summary {
  events: number
  /**
   * The headline: one interaction, counted once. Repeats of the same endpoint
   * by the same person inside `burstWindowMs` collapse into one.
   */
  activity: number
  /** Alias of `activity`, kept so an older cached portal still renders. */
  interactions: number
  /** Before collapsing. `rawInteractions - activity` is the repeat traffic. */
  rawInteractions: number
  repeats: number
  burstWindowMs: number
  views: number
  actions: number
  /** Everything those interactions fanned out into. */
  background: number
  /** Requests generated per interaction. Null when there were none. */
  fanoutRatio: number | null
  navViews: number
  apiCalls: number
  apiErrors: number
  /** Null when no call in the window carried a recorded status. */
  apiErrorRate: number | null
  uniqueUsers: number
  uniqueVisitors: number
  activeSeeds: number
  activeShares: number
  activeWorkspaces: number
  avgDurationMs: number | null
  sessions: number
  range: { from: number; to: number }
}

export interface SeriesPoint {
  period: string
  groupKey: string | null
  events: number
  activity: number
  interactions: number
  navViews: number
  apiCalls: number
  uniqueUsers: number
  uniqueVisitors: number
}

export interface BreakdownRow {
  key: string | number | null
  subKey: string | number | null
  events: number
  activity: number
  interactions: number
  navViews: number
  apiCalls: number
  errors: number
  measured: number
  uniqueUsers: number
  uniqueVisitors: number
  lastSeen: number
}

export interface EventRow {
  id: number
  ts: number
  timestamp: string
  kind: string
  event_name: string | null
  interaction: string | null
  device: string | null
  browser: string | null
  os: string | null
  seed: string | null
  share_id: number | null
  workspace_id: number | null
  version: string | null
  app: string | null
  path: string
  method: string
  status: number | null
  duration_ms: number | null
  email: string | null
  domain: string | null
  visitor_id: string | null
  country: string | null
  city: string | null
  attribution: string
  /** Landing `landing_enter` only; see `ActivityRow.screen`. */
  screen_w?: number | null
  screen_h?: number | null
  viewport_w?: number | null
  viewport_h?: number | null
  dpr?: number | null
}

export interface GuideRow {
  guideKey: string
  guideVersion: string | null
  revision: number | null
  seed: string | null
  opens: number
  uniqueViewers: number
  uniqueVisitors: number
  watchedMs: number
  lastSeen: number
}

export interface GuideSequenceRow {
  guideKey: string
  sequence: string
  viewers: number
  watchedMs: number
  furthestMs: number
}

export function buildQuery(
  filters: AnalyticsFilters,
  extra: Record<string, string | number | undefined> = {},
): string {
  const params = new URLSearchParams()
  const set = (key: string, value: unknown) => {
    if (value === undefined || value === null || value === '') return
    params.set(key, String(value))
  }
  set('from', filters.from)
  set('to', filters.to)
  set('seed', filters.seed)
  set('shareId', filters.shareId)
  set('workspaceId', filters.workspaceId)
  set('visitorId', filters.visitorId)
  set('domain', filters.domain)
  set('eventName', filters.eventName)
  if (filters.excludeInternal) params.set('excludeInternal', 'true')
  // Repeated params, one per choice — the worker accepts either that or a
  // comma-joined value.
  const appendAll = (key: string, values: string[] | undefined) => {
    for (const v of values ?? []) params.append(key, v)
  }
  appendAll('version', filters.version)
  appendAll('app', filters.app)
  appendAll('email', filters.email)
  appendAll('country', filters.country)
  appendAll('device', filters.device)
  appendAll('platform', filters.platform)
  appendAll('browser', filters.browser)
  appendAll('kind', filters.kind)
  appendAll('interaction', filters.interaction)
  appendAll('rawInteraction', filters.rawInteraction)
  for (const [key, value] of Object.entries(extra)) set(key, value)
  return params.toString()
}

/** Stable dependency key, so a freshly-built filters object doesn't refetch forever. */
function useFilterKey(filters: AnalyticsFilters, extra: Record<string, string | number | undefined> = {}): string {
  const filterJson = JSON.stringify(filters)
  const extraJson = JSON.stringify(extra)
  return useMemo(() => buildQuery(filters, extra), [filterJson, extraJson])
}

interface FetchState<T> {
  data: T | null
  loading: boolean
  error: string | null
}

/**
 * Fetch JSON, cancelling the in-flight request whenever the query changes.
 * Without the sequence guard a slow early response can land after a fast later
 * one and overwrite the dashboard with stale numbers.
 */
function useJson<T>(url: string | null): FetchState<T> & { refetch: () => void } {
  const [state, setState] = useState<FetchState<T>>({ data: null, loading: !!url, error: null })
  const [nonce, setNonce] = useState(0)
  const latest = useRef(0)

  useEffect(() => {
    if (!url) {
      setState({ data: null, loading: false, error: null })
      return
    }
    const seq = ++latest.current
    const controller = new AbortController()
    setState(prev => ({ ...prev, loading: true, error: null }))

    fetch(url, { signal: controller.signal })
      .then(async res => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({})) as { error?: string }
          throw new Error(body.error || `HTTP ${res.status}`)
        }
        return res.json() as Promise<T>
      })
      .then(data => { if (seq === latest.current) setState({ data, loading: false, error: null }) })
      .catch((err: unknown) => {
        if (controller.signal.aborted || seq !== latest.current) return
        setState({ data: null, loading: false, error: err instanceof Error ? err.message : 'Unknown error' })
      })

    return () => controller.abort()
  }, [url, nonce])

  const refetch = useCallback(() => setNonce(n => n + 1), [])
  return { ...state, refetch }
}

export function useSummary(filters: AnalyticsFilters) {
  const query = useFilterKey(filters)
  return useJson<Summary>(`/api/analytics/summary?${query}`)
}

export function useTimeseries(
  filters: AnalyticsFilters,
  bucket: 'hour' | 'day' | 'month' = 'day',
  groupBy?: GroupKey,
) {
  const query = useFilterKey(filters, { bucket, groupBy })
  const state = useJson<{ series: SeriesPoint[]; labels: Labels }>(`/api/analytics/timeseries?${query}`)
  return { ...state, series: state.data?.series ?? [], labels: state.data?.labels ?? EMPTY_LABELS }
}

export interface ActionRow {
  seed: string
  /** One row per app: the same endpoint used from two apps is two rows. */
  app: string | null
  method: string
  pathTemplate: string
  /** The human name, or null when nobody has written one yet. */
  label: string | null
  category: string | null
  /** The explicit classification override, if one was chosen. Not the effective one. */
  activityOverride: string | null
  hidden: boolean
  named: boolean
  /** view | action | background | asset — after any dictionary override. */
  activityKind: string
  events: number
  activity: number
  rawInteractions: number
  uniqueUsers: number
  errors: number
  measured: number
  avgDurationMs: number | null
  lastSeen: number
}

export interface ActionsResponse {
  scope: 'activity' | 'all'
  rows: ActionRow[]
  /** Endpoints with no human name — counted across the range, not the page. */
  unnamed: number
  unnamedActivity: number
  totalActivity: number
  /** True when `rows` is a page of a longer list. */
  truncated: boolean
  labels: Labels
}

/**
 * What people did, ranked by collapsed activity.
 *
 * `scope: 'all'` includes background endpoints — needed by the naming screen,
 * because you cannot decide that a poll is a poll if the list hides it.
 */
export function useActions(
  filters: AnalyticsFilters,
  opts: { scope?: 'activity' | 'all'; includeHidden?: boolean; limit?: number } = {},
) {
  const { scope = 'activity', includeHidden, limit = 100 } = opts
  const query = useFilterKey(filters, {
    scope,
    includeHidden: includeHidden ? 'true' : undefined,
    limit,
  })
  const state = useJson<ActionsResponse>(`/api/analytics/actions?${query}`)
  return {
    ...state,
    rows: state.data?.rows ?? [],
    unnamed: state.data?.unnamed ?? 0,
    unnamedActivity: state.data?.unnamedActivity ?? 0,
    totalActivity: state.data?.totalActivity ?? 0,
    truncated: state.data?.truncated ?? false,
  }
}

export interface ActionOverride {
  seed: string
  method: string
  pathTemplate: string
  label: string
  category?: string | null
  activity?: string | null
  hidden?: boolean
}

/**
 * Name an endpoint — or stop naming it.
 *
 * Applies at read time over rows retained indefinitely, so a name written now
 * describes the whole history of that endpoint, not just its future.
 */
export async function saveActionOverride(override: ActionOverride): Promise<void> {
  const res = await fetch('/api/analytics/dictionary', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(override),
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || `HTTP ${res.status}`)
  }
}

export async function removeActionOverride(
  key: { seed: string; method: string; pathTemplate: string },
): Promise<void> {
  const params = new URLSearchParams({
    seed: key.seed,
    method: key.method,
    pathTemplate: key.pathTemplate,
  })
  const res = await fetch(`/api/analytics/dictionary?${params}`, { method: 'DELETE' })
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string }
    throw new Error(body.error || `HTTP ${res.status}`)
  }
}

/** `enabled: false` skips the request entirely — hooks can't be conditional. */
export function useBreakdown(
  filters: AnalyticsFilters,
  by: GroupKey,
  thenBy?: GroupKey,
  limit = 50,
  enabled = true,
) {
  const query = useFilterKey(filters, { by, thenBy, limit })
  const state = useJson<{ rows: BreakdownRow[]; labels: Labels }>(
    enabled ? `/api/analytics/breakdown?${query}` : null,
  )
  return { ...state, rows: state.data?.rows ?? [], labels: state.data?.labels ?? EMPTY_LABELS }
}

export function useGuideAnalytics(filters: AnalyticsFilters) {
  const query = useFilterKey(filters)
  const state = useJson<{ guides: GuideRow[]; sequences: GuideSequenceRow[]; labels: Labels }>(
    `/api/analytics/guides?${query}`,
  )
  return {
    ...state,
    guides: state.data?.guides ?? [],
    sequences: state.data?.sequences ?? [],
    labels: state.data?.labels ?? EMPTY_LABELS,
  }
}

export interface LocationPoint {
  country: string
  city: string | null
  latitude: number | null
  longitude: number | null
  events: number
  interactions: number
  uniqueUsers: number
  lastSeen: number
}

export function useLocations(filters: AnalyticsFilters) {
  const query = useFilterKey(filters)
  const state = useJson<{ locations: LocationPoint[] }>(`/api/analytics/locations?${query}`)
  return { ...state, locations: state.data?.locations ?? [] }
}

export function useDimensionValues(filters: AnalyticsFilters, of: GroupKey) {
  const query = useFilterKey(filters, { of })
  const state = useJson<{ values: Array<{ value: string; events: number }> }>(`/api/analytics/dimensions?${query}`)
  return { ...state, values: state.data?.values ?? [] }
}

/**
 * Keyset-paginated list. `loadMore` appends; changing the query resets.
 * There is no ceiling on how deep you can page — the old endpoint stopped at
 * 1,000 rows and offered no cursor at all.
 */
function useKeysetFeed<T>(endpoint: string, query: string, field: string) {
  const [pages, setPages] = useState<T[][]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [labels, setLabels] = useState<Labels>(EMPTY_LABELS)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [exhausted, setExhausted] = useState(false)

  // Generation guard. Without it, a page still in flight when the filters
  // change lands afterwards and appends rows from the OLD query to the new
  // result — a feed showing a mix of two filters, which reads as a bug in the
  // data rather than in the client.
  const generation = useRef(0)
  const controller = useRef<AbortController | null>(null)

  const load = useCallback(async (before: string | null, append: boolean) => {
    controller.current?.abort()
    const abort = new AbortController()
    controller.current = abort
    const seq = ++generation.current

    setLoading(true)
    setError(null)
    try {
      const res = await fetch(
        `${endpoint}?${query}${before ? `&before=${encodeURIComponent(before)}` : ''}`,
        { signal: abort.signal },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(body.error || `HTTP ${res.status}`)
      }
      const data = await res.json() as Record<string, unknown> & { nextCursor: string | null; labels?: Labels }
      if (seq !== generation.current) return
      const rows = (data[field] as T[] | undefined) ?? []
      setPages(prev => (append ? [...prev, rows] : [rows]))
      setCursor(data.nextCursor)
      setExhausted(data.nextCursor === null)
      setLabels(data.labels ?? EMPTY_LABELS)
    } catch (err) {
      if (abort.signal.aborted || seq !== generation.current) return
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      if (seq === generation.current) setLoading(false)
    }
  }, [endpoint, query, field])

  useEffect(() => {
    // Reset synchronously so a stale page can't linger under the new filters.
    setPages([])
    setCursor(null)
    setExhausted(false)
    void load(null, false)
    return () => controller.current?.abort()
  }, [load])

  const loadMore = useCallback(() => { if (cursor !== null) void load(cursor, true) }, [cursor, load])
  const rows = useMemo(() => pages.flat(), [pages])

  return { rows, labels, loading, error, exhausted, loadMore }
}

/** The raw request feed — the technical Explorer. */
export function useEventFeed(filters: AnalyticsFilters, pageSize = 100) {
  const query = useFilterKey(filters, { limit: pageSize })
  const { rows, ...rest } = useKeysetFeed<EventRow>('/api/analytics/events', query, 'events')
  return { events: rows, ...rest }
}

export interface ActivityRow {
  id: number
  ts: number
  seed: string | null
  shareId: number | null
  workspaceId: number | null
  version: string | null
  app: string | null
  email: string | null
  kind: string
  eventName: string | null
  method: string
  path: string
  pathTemplate: string | null
  /** view | action | background | asset — after any dictionary override. */
  activityKind: string | null
  /** The human name, or null when nobody has written one yet. */
  label: string | null
  category: string | null
  hidden: boolean
  /** Inside a minute of the same request by the same person. Only listed at `scope: 'all'`. */
  repeat: boolean
  status: number | null
  /**
   * The browser's screen, window and pixel ratio (CSS px, exact), plus the
   * capture's device model, device class and OS, captured at landing sign-in. `inherited` when this row did not carry it and it is the
   * person's latest earlier capture; null when there is none.
   */
  screen: ScreenValue | null
}

/**
 * Who did what, when — one row per thing done, newest first.
 *
 * `scope: 'all'` also lists what the counts leave out (repeats, plumbing,
 * hidden endpoints), flagged on each row.
 */
export function useActivityLog(
  filters: AnalyticsFilters,
  opts: { scope?: 'activity' | 'all'; pageSize?: number } = {},
) {
  const { scope = 'activity', pageSize = 100 } = opts
  const query = useFilterKey(filters, { scope, limit: pageSize })
  return useKeysetFeed<ActivityRow>('/api/analytics/activity', query, 'rows')
}

export interface Basemap {
  available: boolean
  tiles?: string
  minZoom?: number
  maxZoom?: number
  attribution?: string
}

/** The self-hosted street map under the origin map. See worker routes/basemap.ts. */
export function useBasemap() {
  return useJson<Basemap>('/api/map/basemap')
}

// ─── Label helpers ──────────────────────────────────────────────
//
// Ids that resolve to nothing are rendered as "Unattributed", never as a blank
// or a zero: roughly half of the backfilled history genuinely has no share,
// because it belongs to seeds that no longer exist.

export function labelForShare(labels: Labels, id: string | number | null | undefined): string {
  if (id === null || id === undefined || id === '') return 'Unattributed'
  return labels.shares[String(id)] || `Share #${id}`
}

export function labelForWorkspace(labels: Labels, id: string | number | null | undefined): string {
  if (id === null || id === undefined || id === '') return 'Unattributed'
  return labels.workspaces[String(id)] || `Workspace #${id}`
}

export function labelForSeed(labels: Labels, name: string | number | null | undefined): string {
  if (name === null || name === undefined || name === '') return 'Unattributed'
  return labels.seeds[String(name)] || String(name)
}
